import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { ContractSigningView } from '../models/contract-signing.types';
import { EdContractSigningApi } from '../tokens/ed-contract-signing.token';

/**
 * REST implementation of the E-D contract-signing API (BRS Ch.3 §3.5, EM-772).
 *
 *   GET  /services/embedded/api/business/ed/contract-signing/{applicationId}
 *   POST /services/embedded/api/business/ed/contract-signing/{applicationId}/upload
 *   GET  /services/embedded/api/business/ed/contract-signing/{applicationId}/mobile-app-qr
 *
 * REST-only (Rules 07/10). Bound to ED_CONTRACT_SIGNING in mock-data.providers.ts.
 */
@Injectable({ providedIn: 'root' })
export class EdContractSigningRestService implements EdContractSigningApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_PATHS.EMBEDDED_API_BASE}/business/ed/contract-signing`;

  getView(applicationId: number): Observable<ContractSigningView> {
    return this.http.get<ContractSigningView>(`${this.base}/${applicationId}`);
  }

  downloadContract(applicationId: number): Observable<HttpResponse<Blob>> {
    // Streamed through HttpClient (not window.open) so the auth interceptor attaches the bearer
    // token — the endpoint is authenticated and party-scoped server-side.
    return this.http.get(`${this.base}/${applicationId}/document`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  uploadSignedContract(applicationId: number, file: File): Observable<ContractSigningView> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<ContractSigningView>(`${this.base}/${applicationId}/upload`, form);
  }

  getMobileAppQr(applicationId: number): Observable<{ qrPayload: string }> {
    return this.http.get<{ qrPayload: string }>(`${this.base}/${applicationId}/mobile-app-qr`);
  }
}
